import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Pause, Play, Captions, SkipForward, CheckCircle2, ListVideo, Hand, Star, Flag, Maximize2, Minimize2 } from "lucide-react";
import { useFullscreen } from "@/lib/useFullscreen";
import { useLang } from "@/context/LanguageContext";
import { hi } from "@/lib/classroom_hi";
import { quizChapter, getStars, saveStars } from "@/lib/classroom_quiz";
import { QuizCheckpoint } from "@/components/QuizCheckpoint";
import { ShareToClassroom } from "@/components/ShareToClassroom";
import { ClassStart } from "@/components/ClassStart";
import { TeacherCam } from "@/components/TeacherCam";
import { Whiteboard } from "@/components/Whiteboard";
import { ClassSummary } from "@/components/ClassSummary";
import { getPresent, presentToday, beatWatch } from "@/lib/classroom_live";
import { lessonSrc, useClassPrefs } from "@/lib/smart";
import NOTES from "@/lib/classroom_notes.json";

export const fmtMin = (sec) => `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, "0")}`;
const WATCHED = "rs_classroom_watched";
export const getWatched = () => { try { return JSON.parse(localStorage.getItem(WATCHED) || "[]"); } catch { return []; } };

const Btn = ({ onClick, label, testid, children, active }) => (
  <button onClick={onClick} aria-label={label} title={label} data-testid={testid}
    className={`w-10 h-10 rounded-full grid place-items-center border backdrop-blur-md shadow-md transition-[background-color,transform] active:scale-95 ${active ? "bg-[#FF3B3B] border-[#FF3B3B] text-white" : "bg-white/85 border-white text-[#0A192F] hover:bg-white"}`}>
    {children}
  </button>
);

export const ClassroomPlayer = ({ lessons, index, setIndex, onWatched, onRaiseHand, controlRef, audio, me }) => {
  const { L } = useLang();
  const v = useRef(null);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const [cc, setCc] = useState(false);
  const [cue, setCue] = useState(false);
  const [t, setT] = useState(0);
  const [quizOpen, setQuizOpen] = useState(false);
  const [started, setStarted] = useState({});
  const [skipAll, setSkipAll] = useState(false);
  const [summary, setSummary] = useState(false);
  const asked = useRef(new Set());
  const chipsRef = useRef(null);
  const screen = useRef(null);
  const fs = useFullscreen(screen);
  const l = lessons[index];
  const qc = quizChapter(l);
  const cards = NOTES[l.slug] || [];
  const gate = !skipAll && !started[l.slug] && !presentToday(l.slug);
  const presence = presentToday(l.slug) ? getPresent()[l.slug] : null;
  const lowbw = useClassPrefs((x) => x.lowbw);
  if (controlRef) controlRef.current = { pause: () => v.current?.pause() };
  // One Zoya voice at a time: the lesson pauses when live Zoya speaks, and playing the lesson silences live Zoya.
  useEffect(() => { if (audio.speaking && v.current && !v.current.paused) v.current.pause(); }, [audio.speaking]);
  // Quiz, summary and raise-hand open outside the video, so fullscreen closes first.
  useEffect(() => { if (quizOpen || summary) fs.exit(); }, [quizOpen, summary]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastIdx = useRef(index);
  useEffect(() => {
    const el = v.current;
    if (lastIdx.current === index && t > 1) el.currentTime = t; // low-data switch keeps the place in the lesson
    lastIdx.current = index;
    el.muted = muted;
    if (gate) el.pause(); else play();
    const tr = el.textTracks[0];
    if (tr) {
      tr.mode = cc ? "showing" : "hidden";
      tr.oncuechange = () => setCue((tr.activeCues?.length || 0) > 0);
    }
    setCue(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, lowbw]);

  const play = () => v.current.play().then(() => setPaused(false)).catch(() => setPaused(true));
  const unmute = () => { v.current.muted = false; setMuted(false); play(); };
  const toggleMute = () => { const el = v.current; el.muted = !el.muted; setMuted(el.muted); };
  const togglePlay = () => { if (v.current.paused) play(); else { v.current.pause(); setPaused(true); } };
  const toggleCc = () => { const tr = v.current.textTracks[0]; if (tr) tr.mode = cc ? "hidden" : "showing"; setCc(!cc); };
  const next = () => setIndex((index + 1) % lessons.length);
  const ended = () => { onWatched(l.slug); beatWatch(l.slug, v.current.currentTime, true).then(() => setSummary(true)); };
  const seek = (s) => { v.current.currentTime = s + 0.05; setT(s + 0.05); play(); };
  const current = [...l.chapters].reverse().find((c) => c.t <= t + 0.1);
  const ci = Math.max(0, l.chapters.indexOf(current));
  useEffect(() => {
    const box = chipsRef.current, chip = box?.children[ci];
    if (chip && box.scrollWidth > box.clientWidth) box.scrollTo({ left: box.scrollLeft + chip.getBoundingClientRect().left - box.getBoundingClientRect().left - 16, behavior: "smooth" });
  }, [ci]);
  const onTime = (e) => {
    const now = e.target.currentTime;
    setT(now);
    beatWatch(l.slug, now);
    if (qc && !asked.current.has(l.slug) && now >= qc.t + 0.3 && now < qc.t + 20) {
      asked.current.add(l.slug);
      e.target.pause();
      setQuizOpen(true);
    }
  };
  const quizDone = (score) => { saveStars(l.slug, score); setQuizOpen(false); play(); };
  const raiseHand = () => { v.current.pause(); fs.exit(); onRaiseHand?.({ chapter: current?.title || "", at: Date.now() }); };
  const start = (info) => {
    setStarted((s) => ({ ...s, [l.slug]: true }));
    if (!info) setSkipAll(true);
    unmute();
  };
  const endClass = () => { v.current.pause(); beatWatch(l.slug, v.current.currentTime, true).then(() => setSummary(true)); };
  const closeSummary = (then) => { setSummary(false); then?.(); };
  const speaking = audio.speaking || (!paused && !muted && cue);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 scroll-mt-24" data-testid="classroom-player">
      <div className="min-w-0">
        <div ref={screen} className={fs.full ? `bg-black flex items-center justify-center ${fs.css ? "fixed inset-0 z-[120]" : "w-full h-full"}` : "relative"} data-testid="classroom-screen" data-full={fs.full ? "1" : "0"}>
        <div className={fs.full ? "relative w-full h-full" : "relative rounded-3xl overflow-hidden border border-slate-200 bg-[#D6E6FA] aspect-video shadow-[0_40px_120px_-30px_rgba(10,25,47,0.35)]"}>
          <video key={`${l.slug}-${lowbw}`} ref={v} className={`w-full h-full ${fs.full ? "object-contain" : "object-cover"}`} autoPlay={!gate} muted playsInline preload={lowbw ? "none" : "metadata"} poster={l.poster}
            onEnded={ended} onTimeUpdate={onTime} onPlay={() => { audio.stop(); setPaused(false); beatWatch(l.slug, v.current.currentTime, true); }} onPause={() => setPaused(true)}
            data-testid="classroom-video" aria-label={l.title}>
            <source src={lessonSrc(l, lowbw)} type="video/mp4" />
            <track kind="captions" src={l.vtt} srcLang="hi" label="Hinglish" />
          </video>
          {!gate && muted && (
            <button onClick={unmute} data-testid="classroom-unmute-big"
              className="absolute left-1/2 top-[40%] sm:top-1/2 -translate-x-1/2 -translate-y-1/2 inline-flex items-center gap-2 sm:gap-3 whitespace-nowrap px-4 py-2.5 sm:px-7 sm:py-4 rounded-full bg-[#FF3B3B] hover:bg-[#E63946] text-white font-extrabold text-sm sm:text-lg shadow-[0_20px_60px_-10px_rgba(255,59,59,0.8)] animate-pulse">
              <Volume2 className="w-4 h-4 sm:w-6 sm:h-6" />{L("Aawaz chalu karein", "आवाज़ चालू करें")}
            </button>
          )}
          {!gate && (
            <div className="absolute bottom-3 right-3 flex gap-2">
              <Btn onClick={raiseHand} label={L("Raise hand: ask Zoya Ma'am", "हाथ उठाएँ: ज़ोया मैम से पूछें")} testid="classroom-raise-hand-btn"><Hand className="w-5 h-5" /></Btn>
              <Btn onClick={toggleCc} label="Captions" testid="classroom-cc-btn" active={cc}><Captions className="w-5 h-5" /></Btn>
              <Btn onClick={togglePlay} label={paused ? "Play" : "Pause"} testid="classroom-pause-btn">{paused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}</Btn>
              <Btn onClick={toggleMute} label={muted ? "Unmute" : "Mute"} testid="classroom-mute-btn" active={!muted}>{muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</Btn>
              <Btn onClick={endClass} label={L("End class: see your summary", "क्लास ख़त्म करें: सारांश देखें")} testid="classroom-end-btn"><Flag className="w-5 h-5" /></Btn>
              <Btn onClick={next} label="Next lesson" testid="classroom-next-btn"><SkipForward className="w-5 h-5" /></Btn>
              <Btn onClick={fs.toggle} label={fs.full ? L("Exit fullscreen (Esc)", "फ़ुलस्क्रीन बंद (Esc)") : L("Fullscreen", "फ़ुलस्क्रीन")} testid="classroom-fullscreen-btn" active={fs.full}>{fs.full ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}</Btn>
            </div>
          )}
          <div className="absolute bottom-0 inset-x-0 h-1 bg-white/50"><div className="h-full bg-gradient-to-r from-[#FF3B3B] to-[#FF2E63]" style={{ width: `${(t / l.duration) * 100}%` }} /></div>
        </div>
        {gate && <ClassStart key={l.slug} lesson={l} me={me} audio={audio} onStart={start} />}
        </div>
        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#007A8A]">{L(`Free preview · Lesson ${l.n} · ${l.track}`, `मुफ़्त प्रीव्यू · पाठ ${l.n} · ${hi(l.track)}`)} · <span data-testid="classroom-lesson-duration">{fmtMin(l.duration)}</span></div>
          <h2 className="mt-2 text-2xl md:text-3xl font-extrabold" data-testid="classroom-lesson-title">{L(l.title, l.title_hi)}</h2>
          <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500">{L("with Zoya Ma'am · Hinglish · Class 9 · Learn & Earn level", "ज़ोया मैम के साथ · हिंग्लिश · कक्षा 9 · लर्न एंड अर्न लेवल")}</p>
            <ShareToClassroom lesson={l} compact />
          </div>
        </div>
        <div ref={chipsRef} className="mt-5 -mx-4 px-4 sm:mx-0 sm:px-0 flex flex-nowrap sm:flex-wrap gap-2 overflow-x-auto sm:overflow-visible rs-noscroll snap-x" data-testid="classroom-chapters">
          {l.chapters.map((c, i) => (
            <button key={c.t} onClick={() => seek(c.t)} data-testid={`chapter-${i}`}
              className={`shrink-0 snap-start whitespace-nowrap px-3 py-2 sm:py-1.5 rounded-full text-xs font-semibold border transition-colors ${current?.t === c.t ? "bg-[#00E5FF] text-[#0A192F] border-[#00E5FF]" : "border-slate-300 text-slate-600 hover:border-[#00E5FF] hover:text-[#0A192F]"}`}>
              {fmtMin(c.t)} · {L(c.title, hi(c.title))}
            </button>
          ))}
        </div>
        <Whiteboard lesson={l} cards={cards} live={ci} onSeek={(i) => seek(l.chapters[i]?.t || 0)} />
      </div>
      <div className="space-y-4">
        <TeacherCam lesson={l} speaking={speaking} levelRef={audio.speaking ? audio.levelRef : null} playing={!paused} presence={presence} first={me.first} />
        <Playlist lessons={lessons} index={index} setIndex={setIndex} />
      </div>
      {quizOpen && <QuizCheckpoint slug={l.slug} teacher={l.teacher || "/classroom/zoya.jpg"} play={audio.play} onDone={quizDone} />}
      {summary && <ClassSummary lesson={l} cards={cards} name={me.name} first={me.first} present={Boolean(presence)} watched={getWatched().includes(l.slug)}
        total={{ watched: getWatched().length, lessons: lessons.length }} audio={audio}
        onNext={() => closeSummary(next)} onClose={() => closeSummary()}
        onAsk={() => closeSummary(() => document.querySelector('[data-testid="ask-zoya"]')?.scrollIntoView({ behavior: "smooth" }))} />}
    </div>
  );
};

const Playlist = ({ lessons, index, setIndex }) => {
  const { L } = useLang();
  const watched = getWatched();
  const stars = getStars();
  return (
    <aside className="rounded-3xl border border-slate-200 bg-[#E8F0FE] p-4 h-fit" data-testid="classroom-playlist">
      <div className="flex items-center gap-2 px-2 pb-3 font-bold"><ListVideo className="w-5 h-5 text-[#FF2E63]" />{L("Free preview lessons", "मुफ़्त प्रीव्यू पाठ")} <span className="text-xs text-slate-500 font-medium">· {lessons.length}</span></div>
      <ul className="space-y-2">
        {lessons.map((x, i) => (
          <li key={x.slug}>
            <button onClick={() => setIndex(i)} data-testid={`playlist-item-${i}`}
              className={`w-full flex gap-3 p-2 rounded-2xl text-left transition-colors ${i === index ? "bg-white ring-1 ring-[#FF2E63]" : "hover:bg-slate-900/5"}`}>
              <div className="relative w-28 shrink-0 aspect-video rounded-xl overflow-hidden bg-[#D6E6FA]">
                <img src={x.poster} alt="" loading="lazy" className="w-full h-full object-cover" />
                <span className="absolute bottom-1 right-1 text-[10px] font-bold bg-white/90 text-[#0A192F] px-1.5 rounded">{fmtMin(x.duration)}</span>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[#007A8A] font-bold">{L(`Lesson ${x.n}`, `पाठ ${x.n}`)}</div>
                <div className="text-sm font-semibold leading-snug line-clamp-2">{L(x.title, x.title_hi)}</div>
                <div className="mt-1 flex items-center gap-3">
                  {watched.includes(x.slug) && <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{L("Watched", "देख लिया")}</span>}
                  {stars[x.slug] > 0 && <span className="text-[11px] text-[#B45309] inline-flex items-center gap-1" data-testid={`playlist-stars-${i}`}><Star className="w-3 h-3 fill-[#FFC94D]" />{stars[x.slug]}/3</span>}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
};
